import { HIDDEN_RECIPES } from '@constants/subscription';
import { translateOutsideReact } from '@shared/i18n/intlInstance';

import type { RecalculatedProductVariant, RecalculateProductResponse } from '../core/types';

const CONFIGURATOR_PET_ACTIVITY = {
  calm: 0.9,
  normal: 1.0,
  active: 1.2,
} as const;

const CONFIGURATOR_PET_BODY = {
  skinny: 1.2,
  ideal: 1.0,
  chunky: 0.8,
} as const;

const CONFIGURATOR_PET_SNACK_LEVEL = {
  none: 1.0,
  some: 0.85,
  lots: 0.6,
} as const;

export const calcScore = (
  weight: number,
  activity: 'calm' | 'normal' | 'active',
  body: 'skinny' | 'ideal' | 'chunky',
  dogStage: 'puppy' | 'adult' | 'senior',
  adultWeight?: number,
  snackLevel: 'none' | 'some' | 'lots' = 'none',
): number => {
  const activityValue = CONFIGURATOR_PET_ACTIVITY[activity];
  const bodyValue = CONFIGURATOR_PET_BODY[body];
  const snackValue = CONFIGURATOR_PET_SNACK_LEVEL[snackLevel];
  const adultCalories = Math.round(
    95 * Math.pow(weight, 0.75) * activityValue * bodyValue * snackValue,
  );

  if (dogStage === 'puppy' && adultWeight && weight <= adultWeight) {
    const puppyCalories = Math.round(
      (1.063 - (0.565 * weight) / adultWeight) * Math.pow(weight, 0.75) * 239 * snackValue,
    );
    // Use the higher of both formulas to prevent decreasing calories
    // as puppy weight approaches adult weight
    return Math.max(puppyCalories, adultCalories);
  }

  return adultCalories;
};

export const getDisabledRecipesFromAllergies = (
  allergies: string[],
): {
  disabledRecipes: string[];
  reasons: { recipe: string; reason: string }[];
} => {
  const allergyRecipeMap: Record<string, string[]> = {
    beef: ['CG'],
    chicken: ['GS'],
    pork: ['RT'],
    // Wildschmaus (Wild) and Ringeltanz (Schwein) both contain potato
    // (Wildschmaus: Kartoffeln 33%), so a potato allergy disables both, mirroring
    // the potato trigger in getDisabledRecipes.ts.
    potato: ['RT', 'WS'],
  };

  // Hidden recipes (e.g. WS) are always disabled so the recalculated product never
  // resolves to or pre-selects them, while the calorie/product calculation itself
  // still runs normally.
  const disabledRecipes: string[] = [...HIDDEN_RECIPES];
  const reasons: { recipe: string; reason: string }[] = [];

  allergies.forEach((allergy) => {
    const recipesToDisable = allergyRecipeMap[allergy.toLowerCase()];
    if (recipesToDisable) {
      recipesToDisable.forEach((recipe) => {
        if (!disabledRecipes.includes(recipe)) {
          disabledRecipes.push(recipe);
          // Ids, not text. The allergy keys are the values the API returns, so
          // they stay untranslated; only the labels they point at are localised.
          const allergyNameIds: Record<string, string> = {
            beef: 'ALLERGEN.BEEF',
            chicken: 'ALLERGEN.CHICKEN',
            pork: 'ALLERGEN.PORK',
            potato: 'ALLERGEN.POTATO',
          };
          const allergyNameId = allergyNameIds[allergy.toLowerCase()];
          reasons.push({
            recipe,
            // `translateOutsideReact` because the only caller is a saga, matching
            // the product-title fallback further down this file.
            reason: translateOutsideReact(
              'RECIPE.DISABLED.ALLERGY_REASON',
              'Enthält {meat} (Allergie)',
              {
                meat: allergyNameId ? translateOutsideReact(allergyNameId, allergy) : allergy,
              },
            ),
          });
        }
      });
    }
  });

  return { disabledRecipes, reasons };
};

const recipesMatch = (recipes1: string[], recipes2: string[]): boolean => {
  if (recipes1.length !== recipes2.length) return false;
  const sorted1 = [...recipes1].sort();
  const sorted2 = [...recipes2].sort();
  return sorted1.every((recipe, index) => recipe === sorted2[index]);
};

const findMatchingVariant = (
  variants: any[],
  currentPreferences?: { recipes: string[]; pension: string; cycle: number },
): any | null => {
  if (!currentPreferences || !variants || variants.length === 0) {
    return null;
  }

  const matchingVariant = variants.find((variant) => {
    const variantRecipes = variant.selectedOptions?.recipes || [];
    const variantPension = variant.selectedOptions?.pension;
    const variantCycle = variant.selectedOptions?.cycle;

    return (
      recipesMatch(variantRecipes, currentPreferences.recipes) &&
      variantPension === currentPreferences.pension &&
      variantCycle === currentPreferences.cycle
    );
  });

  return matchingVariant || null;
};

export const transformRecommendationToProduct = (
  recommendation: any,
  disabledRecipes: string[],
  reasons: { recipe: string; reason: string }[],
  currentPreferences?: { recipes: string[]; pension: string; cycle: number },
): RecalculateProductResponse => {
  const product = recommendation.product || recommendation;

  const apiKcal =
    recommendation.kcal ||
    product.kcal ||
    recommendation.calories?.kcal ||
    product.calories?.kcal ||
    recommendation.calculatedCalories?.kcal;

  const apiKcalMin =
    recommendation.kcal_min ||
    product.kcal_min ||
    recommendation.calories?.kcal_min ||
    product.calories?.kcal_min ||
    recommendation.calculatedCalories?.kcal_min;

  const apiKcalMax =
    recommendation.kcal_max ||
    product.kcal_max ||
    recommendation.calories?.kcal_max ||
    product.calories?.kcal_max ||
    recommendation.calculatedCalories?.kcal_max;

  const kcal = typeof apiKcal === 'string' ? parseInt(apiKcal, 10) : apiKcal || 0;
  const kcal_min =
    typeof apiKcalMin === 'string'
      ? parseInt(apiKcalMin, 10)
      : apiKcalMin || Math.round(kcal * 0.95);
  const kcal_max =
    typeof apiKcalMax === 'string'
      ? parseInt(apiKcalMax, 10)
      : apiKcalMax || Math.round(kcal * 1.05);

  const allVariants = product.variants || [];
  const matchingVariant = findMatchingVariant(allVariants, currentPreferences);

  const variants: RecalculatedProductVariant[] = allVariants.map((variant: any) => ({
    id: String(variant.id || variant.shopify_variant_id || ''),
    title: variant.title || '',
    price: String(variant.price || '0'),
    sku: variant.sku || '',
    selectedOptions: {
      recipes: variant.selectedOptions?.recipes || [],
      pension: variant.selectedOptions?.pension || 'V',
      cycle: variant.selectedOptions?.cycle || 14,
    },
    metafields: variant.metafields || {},
  }));

  const sortedVariants = matchingVariant
    ? [
        variants.find((v) => v.id === String(matchingVariant.id)) || variants[0],
        ...variants.filter((v) => v.id !== String(matchingVariant.id)),
      ]
    : variants;

  return {
    calculatedCalories: {
      kcal,
      kcal_min,
      kcal_max,
    },
    product: {
      id: String(product.id || ''),
      // Rebuilt from the catalogue rather than taken from Shopify, whose title is
      // always German ("Frischfutter für Energiebedarf von 474 kCal/Tag"). It is
      // the same sentence for every fresh product bar the kcal, so it is generated
      // copy, not a product name. Shopify's own title is the fallback for a
      // response with no kcal to rebuild from.
      // `translateOutsideReact` because the single caller is a saga.
      title: kcal
        ? translateOutsideReact(
            'SUBSCRIPTION.FRESH_FOOD_FOR_KCAL',
            `Frischfutter für Energiebedarf von ${kcal} kcal/Tag`,
            { kcal },
          )
        : product.title || '',
      handle: product.handle || '',
      options: product.options || [],
      calories: {
        kcal,
        kcal_min,
        kcal_max,
      },
      variants: sortedVariants,
    },
    restrictions: {
      disabledRecipes,
      reasons,
    },
    requiresProductChange: true,
    currentProductId: String(product.id || ''),
  };
};
